// libs
import * as React from 'react'

// interfaces / constants
import { CLASS_PREFIX } from 'src/constants'

// components
import ItemGroupHeader from 'src/components/item_group_header/item_group_header'
import NavigationItem from 'src/components/navigation_item/navigation_item'
import UserOnboardingCTA from 'src/containers/smart_components/user_onboarding_cta/user_onboarding_cta'
import { URLConsumer } from 'src/containers/url_provider/url_provider'
import { textResources } from 'src/lang/de'

// helpers
import { EventFragment } from '@lopo-tech/lopo-api'
import { POST_DISPLAY_MODE_BANNER, PURPOSE_BASIC } from 'src/interfaces/posts'
import { UrlUtils } from 'src/utils/url/url'
import { EventLocationContainer } from '../shared/ingredients/event_location/event_location_container'
import EventShortDate from '../shared/ingredients/event_short_date/event_short_date'
import Image_with_date from '../shared/ingredients/image_with_date/image_with_date'
import Title from '../shared/ingredients/title/title'
import './event_banner_group.scss'
import { useInView } from 'react-intersection-observer'
import { useEffect } from 'react'

export interface IEventBannerGroupProps {
  eventGroup: EventFragment[];
  title?: string;
  showAllTopLink?: boolean;
  slug?: string;
  onVisibleChange?: (id: string, inView: boolean) => void;
}

export interface IEventBanner {
  onVisibleChange?: (id: string, inView: boolean) => void;
}

const cls = CLASS_PREFIX + 'banner-group'

const EventBanner = ({ id, title, startTime, endTime, canSeeFullContent, attachments, address, slug, __typename, onVisibleChange, format, onlineMeetingUrl }: EventFragment & IEventBanner) => {
  const postDetailsURL = UrlUtils.detailPage(slug, __typename)

  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: '0px 0px -64px 0px'
  })

  useEffect(() => {
    onVisibleChange?.(id, inView)
  }, [id, inView, onVisibleChange])

  return (
    <article key={id} className={cls + '__item'} ref={ref}>
      <UserOnboardingCTA
        active={!canSeeFullContent.value}
        gtmSourceName="postBanner"
      >
        <Image_with_date
          data={{
            dateString: startTime,
            images: attachments
          }}
          displayMode={POST_DISPLAY_MODE_BANNER}
          postId={id}
          purpose={PURPOSE_BASIC}
          postDetailsURL={postDetailsURL}
        />
        <EventShortDate
          displayMode={POST_DISPLAY_MODE_BANNER}
          postType="event"
          data={{ startTime, endTime }}
          postId={id}
          settings={{ show: true }}
          postDetailsURL={postDetailsURL}
        />
        <Title
          displayMode={POST_DISPLAY_MODE_BANNER}
          purpose={PURPOSE_BASIC}
          title={title}
          postDetailsURL={postDetailsURL}
        />
        <EventLocationContainer
          displayMode={POST_DISPLAY_MODE_BANNER}
          purpose={PURPOSE_BASIC}
          data={address}
          postDetailsURL={postDetailsURL}
          format={format}
          // As on the preview card: the address row leads to the detail page,
          // where the fence applies, while the meeting link leads out of the app.
          onlineMeetingUrl={canSeeFullContent.value ? onlineMeetingUrl : undefined}
        />
      </UserOnboardingCTA>
    </article>
  )
}

const EventBannerGroup = ({ eventGroup, showAllTopLink, slug, title, onVisibleChange }: IEventBannerGroupProps) => {
  const showAllClass = cls + '__show-all-link'

  return (
    <div key={title} className={cls}>
      {title && <ItemGroupHeader title={title} spacing>{showAllTopLink
        ? <URLConsumer>
          {({ getEventsfeedRoute }) => (
            <NavigationItem target='_self' url={getEventsfeedRoute(slug)} className={showAllClass}>
              {textResources.shared.showAll}
            </NavigationItem>
          )}
        </URLConsumer>
        : null}</ItemGroupHeader>}
      {eventGroup.map((event) => <EventBanner onVisibleChange={onVisibleChange} key={event.id} {...event}/>)}
    </div>
  )
}

export default EventBannerGroup
